import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { SendQuotationModal } from '@/modules/sales/components/quotations'
import { DataTable } from '@/shared/components/data-table'
import QuoteDeleteConfirmDialog from '@/shared/components/QuoteDeleteConfirmDialog'
import { syncAllAdminData } from '@/shared/lib/crmSync'
import { downloadCsv, rowsToCsv } from '@/shared/lib/exportFormat'
import { toast } from '@/shared/lib/toast'

import {
  deleteAdminQuotation,
  getAdminQuotationById,
  getAdminQuotations,
  getAdminQuotationStats,
} from '../api/quotation.api'
import { getSales } from '../api/sales.api'
import {
  QuoteColumns,
  QuoteDetailsModal,
  QuoteFilters,
  QuoteHeader,
  QuoteMobileCard,
  QuoteSummaryCards,
} from '../components/quotations'

export default function AdminQuotationsPage() {
  const [quotations, setQuotations] = useState([])
  const [quotationMeta, setQuotationMeta] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  })
  const [quotationStats, setQuotationStats] = useState(null)
  const [loading, setLoading] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [searchParams, setSearchParams] = useSearchParams()
  const periodParam = searchParams.get('period') || 'all'
  const startDateParam = searchParams.get('startDate') || ''
  const endDateParam = searchParams.get('endDate') || ''
  const salesPersonParam = searchParams.get('salesPerson') || 'all'

  const [period, setPeriod] = useState(periodParam)

  // Custom date range
  const [startDate, setStartDate] = useState(startDateParam)
  const [endDate, setEndDate] = useState(endDateParam)

  const [appliedStartDate, setAppliedStartDate] = useState(startDateParam)
  const [appliedEndDate, setAppliedEndDate] = useState(endDateParam)

  // Sales person filter
  const [salesPersons, setSalesPersons] = useState([])
  const [salesPerson, setSalesPerson] = useState(salesPersonParam)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPeriod(periodParam)
    if (startDateParam) {
      setStartDate(startDateParam)
      setAppliedStartDate(startDateParam)
    }
    if (endDateParam) {
      setEndDate(endDateParam)
      setAppliedEndDate(endDateParam)
    }
    if (salesPersonParam) {
      setSalesPerson(salesPersonParam)
    }
  }, [periodParam, startDateParam, endDateParam, salesPersonParam])

  const navigate = useNavigate()
  const location = useLocation()

  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [selectedQuote, setSelectedQuote] = useState(null)
  const [isSendOpen, setIsSendOpen] = useState(false)

  // Delete state
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleteQuote, setDeleteQuote] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  /*
   * Fetch Sales Persons
   */
  useEffect(() => {
    const loadSalesPersons = async () => {
      try {
        const response = await getSales({
          page: 1,
          limit: 100,
        })

        setSalesPersons(response?.data || [])
      } catch (error) {
        console.error('Failed to fetch sales persons:', error)

        toast.error(error?.message || 'Failed to fetch sales persons')
      }
    }

    loadSalesPersons()
  }, [])

  /*
   * Debounce search
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, 300)

    return () => clearTimeout(timer)
  }, [search])

  /*
   * Build quotation API params
   */
  const quotationParams = useMemo(
    () => ({
      page: currentPage,
      limit: 5,
      period,

      ...(salesPerson !== 'all'
        ? {
            salesPerson,
          }
        : {}),

      ...(debouncedSearch
        ? {
            search: debouncedSearch,
          }
        : {}),

      ...(period === 'custom'
        ? {
            startDate: appliedStartDate,
            endDate: appliedEndDate,
          }
        : {}),
    }),
    [
      currentPage,
      period,
      salesPerson,
      debouncedSearch,
      appliedStartDate,
      appliedEndDate,
    ],
  )

  /*
   * Fetch quotations + stats
   */
  const loadQuotations = useCallback(
    async (silent = false) => {
      if (period === 'custom' && (!appliedStartDate || !appliedEndDate)) {
        return
      }

      try {
        if (!silent) setLoading(true)

        const [quotationsRes, statsRes] = await Promise.all([
          getAdminQuotations(quotationParams),
          getAdminQuotationStats(quotationParams),
        ])

        setQuotations(quotationsRes?.data || [])

        setQuotationMeta(
          quotationsRes?.meta || {
            page: currentPage,
            limit: 5,
            total: 0,
            totalPages: 0,
          },
        )

        setQuotationStats(statsRes?.data || null)
      } catch (error) {
        if (!silent) toast.error(error?.message || 'Failed to fetch quotations')
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [period, appliedStartDate, appliedEndDate, quotationParams, currentPage],
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadQuotations()
  }, [loadQuotations])

  useEffect(() => {
    const handleGlobalUpdate = () => {
      loadQuotations(true)
    }

    window.addEventListener('crm:data-changed', handleGlobalUpdate)
    window.addEventListener('focus', handleGlobalUpdate)

    return () => {
      window.removeEventListener('crm:data-changed', handleGlobalUpdate)
      window.removeEventListener('focus', handleGlobalUpdate)
    }
  }, [loadQuotations])

  useEffect(() => {
    const state = location.state

    if (!state?.fromQuotationModal || !state?.quotationId) {
      return
    }

    const reopenQuotation = async () => {
      try {
        const response = await getAdminQuotationById(state.quotationId)

        setSelectedQuote(response?.data || response)
        setIsDetailsOpen(true)

        navigate(location.pathname, {
          replace: true,
          state: null,
        })
      } catch (error) {
        toast.error(error?.message || 'Failed to reopen quotation')
      }
    }

    reopenQuotation()
  }, [location, navigate])

  /*
   * Period change
   */
  const handlePeriodChange = (value) => {
    setPeriod(value)
    setCurrentPage(1)

    const updated = new URLSearchParams(searchParams)
    if (value === 'all') {
      updated.delete('period')
      updated.delete('startDate')
      updated.delete('endDate')
    } else {
      updated.set('period', value)
      if (value !== 'custom') {
        updated.delete('startDate')
        updated.delete('endDate')
      }
    }
    setSearchParams(updated)

    if (value !== 'custom') {
      setStartDate('')
      setEndDate('')
      setAppliedStartDate('')
      setAppliedEndDate('')
    }
  }

  /*
   * Sales person change
   */
  const handleSalesPersonChange = (value) => {
    setSalesPerson(value)
    setCurrentPage(1)

    const updated = new URLSearchParams(searchParams)
    if (value === 'all') {
      updated.delete('salesPerson')
    } else {
      updated.set('salesPerson', value)
    }
    setSearchParams(updated)
  }

  /*
   * Apply custom date range
   */
  const handleApplyCustomDate = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates')
      return
    }

    if (startDate > endDate) {
      toast.error('Start date cannot be after end date')
      return
    }

    setAppliedStartDate(startDate)
    setAppliedEndDate(endDate)
    setCurrentPage(1)

    const updated = new URLSearchParams(searchParams)
    updated.set('period', 'custom')
    updated.set('startDate', startDate)
    updated.set('endDate', endDate)
    setSearchParams(updated)
  }

  /*
   * View quotation
   */
  const handleViewQuote = async (quote) => {
    try {
      if (quote?._id) {
        const response = await getAdminQuotationById(quote._id)

        setSelectedQuote(response?.data || quote)
      } else {
        setSelectedQuote(quote)
      }

      setIsDetailsOpen(true)
    } catch (error) {
      toast.error(error?.message || 'Failed to fetch quotation details')
    }
  }

  /*
   * Open send quotation modal
   */
  const handleSendQuote = async (quote) => {
    try {
      if (quote?._id) {
        const response = await getAdminQuotationById(quote._id)
        setSelectedQuote(response?.data || quote)
      } else {
        setSelectedQuote(quote)
      }
      setIsSendOpen(true)
    } catch (error) {
      console.error('Failed to fetch quotation for sending:', error)
      toast.error('Failed to fetch quotation details')
    }
  }

  /*
   * Edit quotation — navigate to the full edit page
   */
  const handleEditQuote = (quote) => {
    navigate(`/admin/quotes/edit/${quote._id}`)
  }

  /*
   * Delete quotation
   */
  const handleDeleteQuote = (quote) => {
    setDeleteQuote(quote)
    setIsDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)

      await deleteAdminQuotation(deleteQuote._id)

      toast.success('Quotation deleted successfully')

      setIsDeleteOpen(false)
      setDeleteQuote(null)

      syncAllAdminData({
        type: 'quotation:deleted',
        quotationId: deleteQuote._id,
      })

      // Refresh list
      const [quotationsRes, statsRes] = await Promise.all([
        getAdminQuotations(quotationParams),
        getAdminQuotationStats(quotationParams),
      ])

      setQuotations(quotationsRes?.data || [])

      setQuotationMeta(
        quotationsRes?.meta || {
          page: currentPage,
          limit: 5,
          total: 0,
          totalPages: 0,
        },
      )

      setQuotationStats(statsRes?.data || null)
    } catch (error) {
      toast.error(error?.message || 'Failed to delete quotation')
    } finally {
      setIsDeleting(false)
    }
  }

  /*
   * Export filtered quotations
   */
  const handleExport = () => {
    const rows = []

    quotations.forEach((quote) => {
      const products =
        Array.isArray(quote.products) && quote.products.length > 0
          ? quote.products
          : [
              {
                product: '-',
                quantity: 0,
              },
            ]

      products.forEach((product, index) => {
        rows.push({
          'Quote No': index === 0 ? (quote.quoteNo ?? '') : '',

          Customer:
            index === 0 ? quote.contactName || quote.customer || '-' : '',

          'Sales Person': index === 0 ? quote.createdBy?.name || '-' : '',

          Product: product.product || '-',

          Quantity: product.quantity ?? 0,

          Total: index === 0 ? quote.grandTotal || 0 : '',

          Status: index === 0 ? quote.status || '' : '',
        })
      })
    })

    if (!rows.length) {
      toast.error('No quotations available to export')
      return
    }

    downloadCsv('admin-quotations.csv', rowsToCsv(rows))
  }

  /*
   * Refresh quotations after sending
   */
  const handleQuotationSent = async () => {
    try {
      const [quotationsRes, statsRes] = await Promise.all([
        getAdminQuotations(quotationParams),
        getAdminQuotationStats(quotationParams),
      ])

      setQuotations(quotationsRes?.data || [])
      setQuotationMeta(
        quotationsRes?.meta || {
          page: currentPage,
          limit: 5,
          total: 0,
          totalPages: 0,
        },
      )
      setQuotationStats(statsRes?.data || null)
    } catch (error) {
      console.error('Error refreshing after sending quote', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <QuoteHeader onExport={handleExport} />

      {/* Summary Cards */}
      <QuoteSummaryCards quotations={quotations} stats={quotationStats} />

      {/* Quotations Table */}
      <div className="rounded-3xl border bg-white p-6 shadow-sm">
        <DataTable
          columns={QuoteColumns(
            handleViewQuote,
            handleSendQuote,
            true,
            handleEditQuote,
            handleDeleteQuote,
          )}
          data={quotations}
          loading={loading}
          search={{
            value: search,
            onChange: (val) => {
              setSearch(val)
              setCurrentPage(1)
            },
            placeholder: 'Search quote #, customer, or sales person...',
          }}
          toolbarExtra={
            <QuoteFilters
              total={quotationMeta?.total ?? 0}
              filtered={quotationMeta?.total ?? 0}
              period={period}
              onPeriodChange={handlePeriodChange}
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onApplyCustomDate={handleApplyCustomDate}
              salesPersons={salesPersons}
              salesPerson={salesPerson}
              onSalesPersonChange={handleSalesPersonChange}
            />
          }
          mobileCard={(row) => (
            <QuoteMobileCard
              quote={row}
              onView={handleViewQuote}
              onSend={handleSendQuote}
            />
          )}
          pagination={{
            page: quotationMeta?.page ?? currentPage,
            pageSize: quotationMeta?.limit ?? 5,
            total: quotationMeta?.total ?? 0,
            onPageChange: (newPage) => setCurrentPage(newPage),
          }}
        />

        {/* Quotation Details */}
        <QuoteDetailsModal
          open={isDetailsOpen}
          onOpenChange={setIsDetailsOpen}
          quote={selectedQuote}
          isAdmin
        />

        {/* Send Quotation */}
        <SendQuotationModal
          open={isSendOpen}
          onOpenChange={setIsSendOpen}
          quotation={selectedQuote}
          onSuccess={handleQuotationSent}
          isAdmin
        />

        {/* Delete Confirm */}
        <QuoteDeleteConfirmDialog
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          quote={deleteQuote}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      </div>
    </div>
  )
}
